import { useMutation } from '@apollo/client';
import {
  ORGANIZATION_NOTIFICATION_PREFERENCES_CREATE_MUTATION,
  ORGANIZATION_NOTIFICATION_PREFERENCES_UPDATE_MUTATION,
} from 'apollo/mutations/organization-notification-preferences-mutations';
import { useApolloUpdate } from 'hooks';
import { map } from 'lodash';
import difference from 'lodash/difference';
import { useCallback } from 'react';
import OrganizationNotificationPreferenceNotification from 'types/enum/OrganizationNotificationPreferenceNotification';
import {
  IOrganizationNotificationPreferences,
  IOrganizationNotificationPreferencesUpdateInput,
} from 'types/OrganizationNotificationPreferences';
import { isoTimeStampUtc } from 'utils/dateUtils';

interface IOrganizationNotificationPreferencesCreateResult {
  createOrganizationNotificationPreferences: IOrganizationNotificationPreferences[];
}

interface IOrganizationNotificationPreferencesUpdateResult {
  updateOrganizationNotificationPreferences: IOrganizationNotificationPreferences[];
}

const createDefault = {
  injuryReport: 0,
  inaccurateTimeReport: 0,
  breakReport: 0,
};

interface SettingChanges {
  originals: IOrganizationNotificationPreferences[];
  updatedMemberIds: string[];
  key: 'breakReport' | 'injuryReport' | 'inaccurateTimeReport';
}

export default function useOrganizationNotificationPreferencesUpdate() {
  const [updateKey] = useApolloUpdate<
    IOrganizationNotificationPreferencesUpdateInput,
    IOrganizationNotificationPreferencesUpdateResult
  >(ORGANIZATION_NOTIFICATION_PREFERENCES_UPDATE_MUTATION, 'organizationNotificationPreferencesUpdateInput');

  const [performMutation] = useMutation<IOrganizationNotificationPreferencesCreateResult>(
    ORGANIZATION_NOTIFICATION_PREFERENCES_CREATE_MUTATION
  );

  return useCallback(
    (settings: SettingChanges) => {
      function newSettingMutations(settings: SettingChanges) {
        const createdOn = isoTimeStampUtc();
        const existingMemberIds = map(settings.originals, 'memberId');
        const newMemberIds = difference(settings.updatedMemberIds, existingMemberIds);

        return newMemberIds.map((memberId) => {
          return performMutation({
            variables: {
              organizationNotificationPreferencesCreateInput: {
                ...createDefault,
                createdOn,
                memberId,
                [settings.key]: OrganizationNotificationPreferenceNotification.PUSH,
              },
            },
          });
        });
      }

      function turnOnExistingMutations(settings: SettingChanges) {
        const existing = settings.originals.filter((original) => {
          return (
            original[settings.key] === OrganizationNotificationPreferenceNotification.OFF &&
            settings.updatedMemberIds.some((updatedMemberId) => original.memberId === updatedMemberId)
          );
        });

        return existing.map(({ id }) => {
          return updateKey(id, settings.key, OrganizationNotificationPreferenceNotification.PUSH);
        });
      }

      function turnOffExistingMutations(settings: SettingChanges) {
        const existing = settings.originals.filter((original) => {
          return (
            original[settings.key] === OrganizationNotificationPreferenceNotification.PUSH &&
            !settings.updatedMemberIds.some((updatedMemberId) => original.memberId === updatedMemberId)
          );
        });

        return existing.map(({ id }) => {
          return updateKey(id, settings.key, OrganizationNotificationPreferenceNotification.OFF);
        });
      }

      return [
        ...newSettingMutations(settings),
        ...turnOnExistingMutations(settings),
        ...turnOffExistingMutations(settings),
      ];
    },
    [performMutation, updateKey]
  );
}
